/**
 * Shrink a profile / listing photo in the browser before the JSON upload
 * proxy. iPhone HEIC and 12MP JPEGs otherwise blow past the body parser.
 * Sharetribe accepts up to 20MB; we send a ~2000px JPEG at quality 0.85.
 */

export const PHOTO_UPLOAD_MAX_EDGE = 2000;
export const PHOTO_UPLOAD_JPEG_QUALITY = 0.85;
export const PHOTO_UPLOAD_MIME = 'image/jpeg';

/**
 * True for HEIC/HEIF files iPhones often send.
 *
 * @param {File|Blob} [file]
 * @returns {boolean}
 */
export const isHeicLike = file => {
  const type = typeof file?.type === 'string' ? file.type.toLowerCase() : '';
  const name = typeof file?.name === 'string' ? file.name.toLowerCase() : '';
  return (
    type.includes('heic') ||
    type.includes('heif') ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
};

/**
 * Fit width/height inside a max edge without upscaling.
 *
 * @param {number} width
 * @param {number} height
 * @param {number} [maxEdge]
 * @returns {{ width: number, height: number }}
 */
export const scaleToMaxEdge = (width, height, maxEdge = PHOTO_UPLOAD_MAX_EDGE) => {
  const w = Number(width);
  const h = Number(height);
  const edge = Number(maxEdge) > 0 ? Number(maxEdge) : PHOTO_UPLOAD_MAX_EDGE;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return { width: 0, height: 0 };
  }
  const longest = Math.max(w, h);
  if (longest <= edge) {
    return { width: Math.round(w), height: Math.round(h) };
  }
  const scale = edge / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
};

const revokeUrl = url => {
  if (url && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
};

/**
 * Decode a File/Blob to a drawable image. HEIC is drawn via createImageBitmap
 * or an Image element (Safari on iPhone).
 *
 * @param {File|Blob} file
 * @returns {Promise<{ source: CanvasImageSource, objectUrl: string|null }>}
 */
export const loadImageSource = async file => {
  if (!file) {
    throw new Error('A photo file is required.');
  }
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, objectUrl: null };
    } catch (e) {
      // Fall through to <img> — some browsers reject HEIC in createImageBitmap.
    }
  }
  const objectUrl = URL.createObjectURL(file);
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read this photo.'));
    img.src = objectUrl;
  });
  return { source: image, objectUrl };
};

const canvasJpegDataUrl = (source, width, height, quality) => {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    throw new Error('Could not process this photo.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not process this photo.');
  }
  ctx.drawImage(source, 0, 0, width, height);
  return canvas.toDataURL(PHOTO_UPLOAD_MIME, quality);
};

/**
 * Read a File as a data URL without resizing.
 *
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('A photo file is required.'));
      return;
    }
    if (typeof FileReader === 'undefined') {
      reject(new Error('Could not read this photo.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read this photo.'));
    reader.readAsDataURL(file);
  });

/**
 * Resize/compress a photo for /api/profile/photo. HEIC is converted by
 * drawing it to a canvas. Falls back to the original data URL only when
 * the browser cannot decode the file and it is not HEIC.
 *
 * @param {File|Blob} file
 * @param {Object} [opts]
 * @param {number} [opts.maxEdge]
 * @param {number} [opts.quality]
 * @returns {Promise<string>}
 */
export const preparePhotoForUpload = async (
  file,
  { maxEdge = PHOTO_UPLOAD_MAX_EDGE, quality = PHOTO_UPLOAD_JPEG_QUALITY } = {}
) => {
  if (!file) {
    throw new Error('A photo file is required.');
  }

  let loaded = null;
  try {
    loaded = await loadImageSource(file);
    const naturalWidth = loaded.source.naturalWidth || loaded.source.width;
    const naturalHeight = loaded.source.naturalHeight || loaded.source.height;
    const size = scaleToMaxEdge(naturalWidth, naturalHeight, maxEdge);
    if (!size.width || !size.height) {
      throw new Error('Could not process this photo.');
    }
    const dataUrl = canvasJpegDataUrl(loaded.source, size.width, size.height, quality);
    if (typeof loaded.source.close === 'function') {
      loaded.source.close();
    }
    revokeUrl(loaded.objectUrl);
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:image/')) {
      return dataUrl;
    }
    throw new Error('Could not process this photo.');
  } catch (e) {
    if (loaded?.objectUrl) {
      revokeUrl(loaded.objectUrl);
    }
    if (isHeicLike(file)) {
      throw e instanceof Error ? e : new Error('Could not process this photo.');
    }
    return fileToDataUrl(file);
  }
};
